import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { SHAPE_FACTSHEET_BLUEPRINT } from '../../shapes'
import Pill from '../../../Pill'
import PageToolbar from '../../../PageToolbar'
import ComponentLoader from '../component-loader'
import { getComponentLabel } from '../../helper'
import Button from '../../../Form/Button'
import Modal from '../../../Modal'
import { withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import activityTrackingService from '../../../../../services/activityTrackingService'
import { trackingEvent } from '../../../../../constants'
import debounce from 'lodash.debounce'
import SearchField from '../../../SearchField'
import './styles.scss'

class SectionGroupContainer extends Component {
  MIN_SEARCH_TEXT_LENGTH = 3
  SEARCHABLE_FACT_TYPES = new Set(['fact:checkbox', 'fact:single-attribute-controlled'])
  state = {
    groupComponentKeys: {},
    selectedGroupKey: null,
    hasGroupedComponents: false,
    showGroupNavigationNotAllowedModal: false,
    searchValue: '',
    filteredComponents: [],
  }

  componentDidMount () {
    this.onBlueprintChange()
  }

  componentDidUpdate (prevProps) {
    if (
      (prevProps.blueprint !== this.props.blueprint)
      || (prevProps.factsheetBlueprint !== this.props.factsheetBlueprint)
    ) {
      this.onBlueprintChange()
    }
  }

  onGroupNavigationNotAllowedModal = (e) => {
    e.preventDefault()
    e.stopPropagation()
    this.setState({ showGroupNavigationNotAllowedModal: false })
  }

  onBlueprintChange () {
    let hasGroupedComponents = false
    const groupComponentKeys = {}
    for (let i = 0; i < this.props.blueprint.components.length; i++) {
      let component = this.props.blueprint.components[i]
      if (component.type === 'section:group') {
        groupComponentKeys[i] = 1
        hasGroupedComponents = true
      }
    }
    this.setState({
      groupComponentKeys: groupComponentKeys,
      selectedGroupKey: hasGroupedComponents ? '0' : null,
      hasGroupedComponents: hasGroupedComponents,
      filteredComponents: [],
    })
  }

  onGroupClick (key, label) {
    activityTrackingService.trackEvent(trackingEvent.clickFactsheetCategory, { field: label })
    if (this.state.selectedGroupKey !== key && !this.props.factsheetIsValid) {
      this.setState({ showGroupNavigationNotAllowedModal: true })
      return
    }
    this.setState({ selectedGroupKey: key })
  }

  hasFactInComponents = (components) => {
    return components.some(component => {
      if (this.SEARCHABLE_FACT_TYPES.has(component.type)) {
        return true
      }
      return this.hasFactInComponents(component.components ?? [])
    })
  }

  componentsFiltered = (components) => {
    const searchVal = this.state.searchValue.toLowerCase()
    return components.map((component) => {
      if (component.type === 'section:separator') {
        return null
      }
      if (this.SEARCHABLE_FACT_TYPES.has(component.type)) {
        const fact = this.props.blueprint.factTree.facts[component.id]
        if (!fact.label.toLowerCase().includes(searchVal)) {
          return null
        }
      }
      const newComponents = component.components
        ? this.componentsFiltered(component.components)
        : []
      if (component.type.startsWith('section:box') || component.type.startsWith('section:group')) {
        if (!this.hasFactInComponents(newComponents)) {
          return null
        }
      }
      return {
        ...component,
        components: newComponents
      }
    }).filter(e => e !== null)
  }

  updateFilteredComponents = debounce((value) => {
    this.setState({
      filteredComponents: this.componentsFiltered(this.props.blueprint.components, value)
    })
  }, 100, {
    leading: false
  })
  trackSearchText = debounce((value) => {
    activityTrackingService.trackEvent(trackingEvent.searchFacts, {
      searchText: value
    })
  }, 500, {
    leading: false,
  })

  render () {
    if (!this.state.hasGroupedComponents) {
      return null
    }
    const searchBox = <SearchField
      onInput={(e) => {
        this.setState({ searchValue: e.target.value })
        if (e.target.value.length < this.MIN_SEARCH_TEXT_LENGTH) {
          return
        }
        this.updateFilteredComponents(e.target.value)
        this.trackSearchText(e.target.value)
      }}
      onClear={() => {
        this.setState({ searchValue: '' })
      }}
      placeholderText={this.props.t('FACTS_SEARCH_PLACEHOLDER')}
    />
    return (
      <React.Fragment>
        {searchBox}
        <br/>
        {
          this.state.searchValue.length < this.MIN_SEARCH_TEXT_LENGTH && <>
            <PageToolbar type="light">
              <div className="Pills">
                {Object.keys(this.state.groupComponentKeys).map((key) => {
                  const sectionId = this.props.blueprint.components[key].id
                  const section = this.props.factsheetBlueprint.factTree.sections[sectionId]
                  const label = getComponentLabel(
                    this.props.blueprint,
                    section)
                  return <Pill
                    key={key}
                    type={this.state.selectedGroupKey === key ? 'darkblue-selected' : 'darkblue'}
                    onClick={() => this.onGroupClick(key, label)}
                  >
                    {label}
                  </Pill>
                })}
              </div>
            </PageToolbar>
            <br/>
            <div className="facts-container">
              {this.props.blueprint.components[this.state.selectedGroupKey].components.map((component, index) => {
                return <ComponentLoader
                  key={index}
                  blueprint={component} parentBlueprint={this.props.blueprint}
                  factsheetBlueprint={this.props.factsheetBlueprint}
                />
              })}
            </div>
            <Modal
              show={this.state.showGroupNavigationNotAllowedModal}
              onEscapeKeyDown={this.onGroupNavigationNotAllowedModal}
              onBackdropClick={this.onGroupNavigationNotAllowedModal}
            >
              <div>
                <p className={'modal-message'}>You must first fix the errors, before you can navigate to a
                  different section.</p>
                <Button
                  classNames={'Button-Light'}
                  align={'right'}
                  onClick={this.onGroupNavigationNotAllowedModal}
                >
                  {this.props.t('BUTTON_OK')}
                </Button>
              </div>
            </Modal>
          </>
        }
        {
          this.state.searchValue.length > this.MIN_SEARCH_TEXT_LENGTH - 1 && <div>
            {this.getSearchResults()}
          </div>
        }
      </React.Fragment>
    )
  }

  getSearchResults = () => {
    return this.state.filteredComponents.length > 0
      ? this.state.filteredComponents.map(component => {
        return component.components
          .map((component, index) => {
            return <div key={index} className="facts-container">
              <ComponentLoader
                key={index}
                blueprint={component} parentBlueprint={this.props.blueprint}
                factsheetBlueprint={this.props.factsheetBlueprint}
              />
            </div>
          })
      })
      : <div>
        <h3
          style={{ textAlign: 'center' }}
        >
          {this.props.t('FACTS_SEARCH_NO_RESULTS', { term: this.state.searchValue })}
        </h3>
      </div>
  }
}

const mapStateToProps = state => {
  return {
    factsheetIsValid: state.factsheet.validation.isValid,
  }
}
SectionGroupContainer.propTypes = {
  blueprint: PropTypes.shape({ components: PropTypes.array.isRequired }).isRequired,
  factsheetBlueprint: PropTypes.shape(SHAPE_FACTSHEET_BLUEPRINT).isRequired
}
export default withTranslation()(connect(
  mapStateToProps,
  null,
)(SectionGroupContainer))
